// composer/SchedulePicker.tsx — date+time popover for scheduling a post.
// Native <input type="datetime-local"> (styled to theme); min = now+5min, max = now+30d.

import { useState } from 'react';
import styled from 'styled-components';
import { Popover, Button } from '@/ui';
import { ComposerTool as IconButton } from './ComposerTool';

const MIN_LEAD_MS = 5 * 60 * 1000;
const MAX_LEAD_MS = 30 * 24 * 60 * 60 * 1000;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 220px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const DateInput = styled.input`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 6px 8px;
  /* Native date/time picker chrome (calendar icon, popup) follows the app theme
     rather than assuming dark, a light-theme member got a dark-on-dark widget. */
  color-scheme: ${({ theme }) => theme.mode};
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

/** Local-time value string for a datetime-local input from a Date. */
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface SchedulePickerProps {
  inline?: boolean;
  onEdit?: () => void;
  /** Called with an ISO datetime once the user confirms a valid pick. */
  onSchedule: (iso: string) => void;
}

export function SchedulePicker({ onSchedule, inline = false, onEdit }: SchedulePickerProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Lazy initializer — reads the clock once, at mount, not on every render.
  const [now] = useState(() => Date.now());
  const min = toLocalInputValue(new Date(now + MIN_LEAD_MS));
  const max = toLocalInputValue(new Date(now + MAX_LEAD_MS));

  function handleConfirm() {
    if (!value) {
      setError('Pick a date and time.');
      return;
    }
    const picked = new Date(value);
    const ms = picked.getTime();
    if (Number.isNaN(ms)) {
      setError('Pick a valid date and time.');
      return;
    }
    // Re-read the clock at confirm time (not the stale mount-time `now`) — the popover
    // can stay open a while before the user picks a time.
    const confirmNow = Date.now();
    if (ms < confirmNow + MIN_LEAD_MS) {
      setError('Schedule at least 5 minutes from now.');
      return;
    }
    if (ms > confirmNow + MAX_LEAD_MS) {
      setError('Schedule within the next 30 days.');
      return;
    }
    setError(null);
    onSchedule(picked.toISOString());
    setOpen(false);
  }

  const controls = (
    <Panel>
      <Label htmlFor="composer-schedule-input">
        Schedule for
        <DateInput
          id="composer-schedule-input"
          type="datetime-local"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            onEdit?.();
            setValue(e.target.value);
            setError(null);
          }}
        />
      </Label>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <Button type="button" variant="primary" size="sm" onClick={handleConfirm}>
        Set schedule
      </Button>
    </Panel>
  );
  if (inline) return controls;
  return (
    <Popover
      trigger={<IconButton icon="calendar-clock" label="Schedule post" size={18} />}
      open={open}
      onOpenChange={setOpen}
    >
      {controls}
    </Popover>
  );
}
